import React, { ReactNode, useContext } from 'react';
import { ConfigContext } from '../../context/Config';
import { BookOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import withSkeleton from '../../hoc/withSkeleton';
import CourseDefaultImg from '../../images/course-default.png';
import Text from '../Text';
import Avatar from '../Avatar';
import Popover from '../Popover';
import ScheduleViewer from '../ScheduleViewer';
import ImageCard from '../ImageCard';
import LevelViewer from '../LevelViewer';
import IconStudentV2 from '../Icons/IconStudentV2';
import CourseCardSkeleton from './CourseCardSkeleton';
import { IconNewCalendar } from '../Icons';
import './CourseCard.less';

// TODO COMPONENT MUST NOT HAVE PROPS STRUCTURE LIKE IN OUR DATABASE
// CHANGE PROP NAMES TO SOMETHING COMMON THAT WILL BE UNDERSTANDABLE FOR OTHERS WHO WILL USE THIS COMPONENTS
export type CourseType = {
  id?: string;
  name?: string | ReactNode;
  description?: string;
  groupTitle?: string;
  startDate?: string;
  endDate?: string;
  teachers?: Array<{
    fullName: string;
  }>;
  teacherName?: string;
  level?: string;
  studentsCount?: number;
  image?: string;
  trainingLinks?: { [key: string]: string };
  practiceLink?: string;
  isFlexible?: boolean;
  price?: string;
  individualLessons?: number;
  individualLessonsType?: string;
  maxStudentsCount?: number;
  courseTeachersName?: Array<{
    fullName: string;
  }>;
  scheduleViewer?: any;
};

export type CourseCardProps = {
  course: CourseType;
  contentHeight?: string;
  image?: string;
  description?: string;
  actions?: ReactNode;
  lessonsCount?: ReactNode;
  customColors?: {
    title?: 'dark-100' | 'black';
    description?: 'dark-100' | 'black';
    price?: 'dark-100' | 'black';
    studentCount?: 'dark-100' | 'black';
  };
  userIconFill?: '';
};

const TCourseCard: React.FC<CourseCardProps> = ({
  course,
  contentHeight = '124px',
  actions,
  lessonsCount,
  customColors,
  userIconFill,
}) => {
  const { translate } = useContext(ConfigContext);

  const {
    teachers = [],
    description = '',
    startDate = '',
    endDate = '',
    level = '',
    studentsCount,
    isFlexible = false,
    price,
    individualLessons,
    individualLessonsType,
    maxStudentsCount,
    courseTeachersName = [],
    scheduleViewer,
  } = course;

  function showTeacher(teachers: any[]) {
    if (!teachers || !teachers.length) {
      return null;
    }

    if (teachers.length > 1) {
      const teachersNames = (
        <div>
          {teachers.map((item: { fullName: boolean }, index: React.Key) => {
            return (
              <Text level={3} rows={1} key={index}>
                {item && item.fullName ? item.fullName : ''}
              </Text>
            );
          })}
        </div>
      );

      return <Popover content={teachersNames}>{translate('Teachers')}</Popover>;
    }

    return (
      <Text level={3} rows={1}>
        {teachers[0] && teachers[0].fullName ? teachers[0].fullName : ''}
      </Text>
    );
  }

  const teacher = showTeacher(teachers);
  const courseTeacherName = showTeacher(courseTeachersName);

  return (
    <ImageCard image={course.image || CourseDefaultImg}>
      {/* Schedule Icon */}
      {!!scheduleViewer && (
        <div className="t-scheduleViewer">
          <div className="t-iconStyle">
            <span className="iconCalendar">
              <Popover
                placement="topRight"
                trigger="click"
                content={scheduleViewer}
              >
                <IconNewCalendar size="md" />
              </Popover>
            </span>
          </div>
        </div>
      )}

      <div style={{ height: contentHeight }} className="t-courseCardContent">
        <div className="t-cardContentRow-1">
          <div className="t-courseCardTitle">
            <Text
              title={String(course?.name)}
              textColor={customColors?.title}
              bold
              level={2}
              rows={1}
            >
              {course?.name}
            </Text>
          </div>

          {!!level ||
            (isFlexible && (
              <div className="t-courseCardScheduleWrapper">
                <ScheduleViewer
                  isFlexible={isFlexible}
                  start={startDate}
                  end={endDate}
                />
                <LevelViewer level={level} />
              </div>
            ))}
        </div>
        {!!description && (
          <div className="t-cardContentRow-2">
            <Text
              textColor={customColors?.description}
              innerText={description}
              readMore
              level={3}
              rows={1}
            />
          </div>
        )}
        <div className="t-cardContentRow-4">
          <div className="t-info-wrapper">
            {!!teachers.length && (
              <div className="t-icon-with-text">
                <Avatar src={''} name={teachers[0]?.fullName} size="xxs" />
                <div className="t-info-text">{teacher}</div>
              </div>
            )}

            {!!studentsCount && (
              <div className="has-margin">
                <div className="t-icon-with-text t-student-count">
                  <IconStudentV2 size="md" fill={userIconFill} />
                  <Text
                    textColor={customColors?.studentCount}
                    level={3}
                    rows={1}
                  >
                    {translate('ui.student.count', {
                      count: studentsCount,
                    })}
                  </Text>
                </div>
              </div>
            )}
            {lessonsCount && (
              <div className="lessons-wrapper">{lessonsCount}</div>
            )}
          </div>

          <div className="t-cardContentRow-individual">
            {/* Student course card teacher name */}
            {courseTeachersName.length > 0 && (
              <div className="t-icon-with-text">
                <Avatar
                  src={''}
                  name={courseTeachersName[0]?.fullName || ''}
                  size="xxs"
                />
                <div className="t-info-text">{courseTeacherName}</div>
              </div>
            )}

            <div className="t-individualLessons">
              {!!individualLessons && (
                <div className="t-individualLessons-wrapper">
                  <BookOutlined />
                  {individualLessons} {translate('Lessons')}
                </div>
              )}

              {!!individualLessonsType && (
                <div className="t-individualLessonsType-wrapper">
                  <UserOutlined />
                  {translate(individualLessonsType)}
                </div>
              )}

              {maxStudentsCount > 0 && (
                <div className="t-maxStudentsCount-wrapper">
                  <TeamOutlined />
                  {maxStudentsCount}{' '}
                  {maxStudentsCount > 0
                    ? translate('Student(s)')
                    : translate('Unlimited')}
                </div>
              )}
            </div>

            {(price || actions) && (
              <div className="t-right-wrapper">
                {price && (
                  <div className="t-price-wrapper">
                    <Text textColor={customColors?.price} level={1}>
                      {price}
                    </Text>
                  </div>
                )}
                {actions && <div className="actions-wrapper">{actions}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </ImageCard>
  );
};

export default withSkeleton(TCourseCard, CourseCardSkeleton);
